"use strict";
/* globals module */
/**
 * @module calendarClient
 * @description  Calendar Client provides an easy way to consume Calendar REST API end-points. In order to obtain needed routes `calendarClient` uses `calendarRoute`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
;
var httpApi_1 = require("../../httpApi");
var _1 = require("./");
var CalendarClient = /** @class */ (function () {
    function CalendarClient(calendarRsvpAttendeeClient, calendarRsvpClient, calendarEventsClient, calendarBatchClient, calendarRoute, apiClient) {
        this.calendarRsvpAttendeeClient = calendarRsvpAttendeeClient;
        this.calendarRsvpClient = calendarRsvpClient;
        this.calendarEventsClient = calendarEventsClient;
        this.calendarBatchClient = calendarBatchClient;
        this.calendarRoute = calendarRoute;
        this.apiClient = apiClient;
    }
    Object.defineProperty(CalendarClient.prototype, "routeDefinition", {
        get: function () {
            return this.calendarRoute;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarClient.prototype, "batch", {
        get: function () {
            return this.calendarBatchClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarClient.prototype, "events", {
        get: function () {
            return this.calendarEventsClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarClient.prototype, "eventRsvp", {
        get: function () {
            return this.calendarRsvpClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarClient.prototype, "eventAttendee", {
        get: function () {
            return this.calendarRsvpAttendeeClient;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns a promise that is resolved once the find action has been performed. Success response returns a list of Calendar resources matching the given criteria.
     * @method
     * @param options Query resource GetCalendarOptions object.
     * @returns A promise that is resolved once the find action has been performed.
     * @example calendarClient.find({
                    pageNumber : 1,
                    pageSize : 10,
                    orderBy : '<field>',
                    orderDirection : '<asc|desc>',
                    search : '<search-phrase>',
                    ids : <identifiers>,
                    ownerIds: <identifiers>,
                    from : <start-date>,
                    to : <end-date>
                })
                .then(function (collection) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarClient.prototype.find = function (options) {
        return this.apiClient.get(this.routeDefinition.find(options));
    };
    /**
     * Returns a promise that is resolved once the get action has been performed. Success response returns the Calendar resource.
     * @method
     * @param id Calendar id which uniquely identifies Calendar resource that needs to be retrieved.
     * @param options Query resource options object.
     * @returns A promise that is resolved once the get action has been performed.
     * @example calendarClient.get()
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarClient.prototype.get = function (id, options) {
        return this.apiClient.get(this.routeDefinition.get(id, options));
    };
    /**
     * Returns a promise that is resolved once the create Calendar action has been performed; this action creates a new Calendar resource.
     * @method
     * @param data A Calendar object that needs to be inserted into the system.
     * @returns A promise that is resolved once the create Calendar action has been performed.
     * @example calendarClient.create({
                    abrv: '<abrv>',
                    description: '<description>',
                    json: '<json>',
                    name: '<name>',
                    owner: <user-info>,
                    ownerId: '<owner-id>'
                })
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarClient.prototype.create = function (data) {
        return this.apiClient.post(this.routeDefinition.create(data), this.routeDefinition.createParams(data));
    };
    /**
     * Returns a promise that is resolved once the update Calendar action has been performed. This action updates a Calendar resource. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `calendarEventRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(calendar);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data A Calendar object used to update specified Calendar resource.
     * @returns A promise that is resolved once the update Calendar action has been performed.
     * @example calendar is a resource previously fetched using get action.
                calendar.name = '<name>';
                calendarClient.update(calendar)
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarClient.prototype.update = function (data) {
        return this.apiClient.put(this.routeDefinition.update(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the remove action has been performed. This action will remove a Calendar resource from the system if successfully completed. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `calendarEventRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(calendar);
     * let uri = params['model'].links('delete').href;
     * ```
     * @method
     * @param data An calendart object used to delete specified Calendar resource.
     * @returns A promise that is resolved once the remove action has been performed.
     * @example calendar is a resource previously fetched using get action.
                calendarClient.remove(calendar)
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarClient.prototype.remove = function (data) {
        return this.apiClient.delete(this.routeDefinition.delete(data));
    };
    /**
     * Returns a promise that is resolved once the purge action has been performed. This action will remove all Calendar resources from the system if succesfully completed.
     * @method
     * @returns A promise that is resolved once the purge action has been performed.
     * @example calendarClient.purge()
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarClient.prototype.purge = function () {
        return this.apiClient.delete(this.routeDefinition.purge());
    };
    var _a, _b, _c, _d, _e, _f;
    CalendarClient = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(_1.TYPES.CalendarRsvpAttendeeClient)),
        tslib_1.__param(1, inversify_1.inject(_1.TYPES.CalendarRsvpClient)),
        tslib_1.__param(2, inversify_1.inject(_1.TYPES.CalendarEventsClient)),
        tslib_1.__param(3, inversify_1.inject(_1.TYPES.CalendarBatchClient)),
        tslib_1.__param(4, inversify_1.inject(_1.TYPES.CalendarRoute)),
        tslib_1.__param(5, inversify_1.inject(httpApi_1.httpTYPES.ApiClient)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof _1.CalendarRsvpAttendeeClient !== "undefined" && _1.CalendarRsvpAttendeeClient) === "function" ? _a : Object, typeof (_b = typeof _1.CalendarRsvpClient !== "undefined" && _1.CalendarRsvpClient) === "function" ? _b : Object, typeof (_c = typeof _1.CalendarEventsClient !== "undefined" && _1.CalendarEventsClient) === "function" ? _c : Object, typeof (_d = typeof _1.CalendarBatchClient !== "undefined" && _1.CalendarBatchClient) === "function" ? _d : Object, typeof (_e = typeof _1.CalendarRoute !== "undefined" && _1.CalendarRoute) === "function" ? _e : Object, typeof (_f = typeof httpApi_1.ApiClient !== "undefined" && httpApi_1.ApiClient) === "function" ? _f : Object])
    ], CalendarClient);
    return CalendarClient;
}());
exports.CalendarClient = CalendarClient;
/**
 * @copyright (c) 2017 Mono Ltd
 * @license MIT
 * @author Mono Ltd
 * @overview
 ***Notes:**
 - Refer to the [Baasic REST API](http://dev.baasic.com/api/reference/home) for detailed information about available Baasic REST API end-points.
 - All end-point objects are transformed by the associated route definition.
 */ 
